import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllAgencyUsers.graphql';
import { Query, QueryallAgencyUsersArgs } from '../../__generated__/globalTypes';

export const useAllAgencyUsersQuery = (options?: QueryHookOptions<Query, QueryallAgencyUsersArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallAgencyUsersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllAgencyUsersLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallAgencyUsersArgs>
): QueryResult<Query, QueryallAgencyUsersArgs> & {
  getAllAgencyUsers: LazyQueryExecFunction<Query, QueryallAgencyUsersArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllAgencyUsers, result] = useLazyQuery<Query, QueryallAgencyUsersArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllAgencyUsers, ...result };
};

export const useAllAgencyUsersPromiseQuery = () => {
  const client = useApolloClient();

  const getAllAgencyUsers = useCallback(
    (options?: Omit<QueryOptions<QueryallAgencyUsersArgs, Query>, 'query'>) =>
      client.query<Query, QueryallAgencyUsersArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllAgencyUsers };
};
// Suspense Query Hooks
export const useAllAgencyUsersSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallAgencyUsersArgs>) => {
  const result = useSuspenseQuery<Query, QueryallAgencyUsersArgs>(QUERY, {
    ...options
  });

  return result;
};
